import SvgIconDownload from '@assets/images/common/icon/chat-action/SvgIconDownload';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  contactApiClient,
  ContactCsvExportRequest,
} from '@src/api/tenant/contact/contactApiClient';
import { Contact, CONTACT_STATUS, ContactStatus, ContactType } from '@src/api/types/contact';
import { Pagination } from '@src/api/types/pagination';
import PaginationComponent from '@src/components/Pagination';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import {
  ContactStatusDropdown,
  ContactTypeBadge,
} from '@src/features/tenant/contact/components/contactBadges';
import { useToast } from '@src/features/toast/context/toastContext';
import { useQueryParams } from '@src/hooks/useQueryParams';
import { TENANT_URL } from '@src/routes/tenant.tsx';
import Button from '@src/ui/elements/button/Button';
import Th from '@src/ui/elements/table/Th';
import Title from '@src/ui/elements/typography/Title.tsx';
import logger from '@src/utils/logger';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

type FilterOption = { value: string; label: string };

function LabeledDropdown({
  options,
  value,
  onChange,
  prefix = '',
  dotClassName,
  width = 150,
}: {
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
  prefix?: string;
  dotClassName?: (value: string) => string;
  width?: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedOption = options.find((option) => option.value === value) ?? options[0];

  return (
    <div
      ref={dropdownRef}
      className='relative'
      style={{ width }}
    >
      <button
        type='button'
        onClick={() => setIsOpen((prev) => !prev)}
        className='flex w-full items-center justify-between gap-2 rounded-md border border-solid border-gray-200 bg-white px-3 py-[10px] text-left text-sm'
      >
        <span className='flex items-center gap-2 truncate'>
          {dotClassName && (
            <span
              className={`size-2 shrink-0 rounded-full ${dotClassName(selectedOption.value)}`}
            />
          )}
          {prefix}
          {selectedOption.label}
        </span>
        <div
          className='h-[5px] w-[10px] shrink-0 bg-black'
          style={{ clipPath: 'polygon(50% 100%, 0 10%, 100% 10%)' }}
        />
      </button>

      {isOpen && (
        <div
          className='absolute z-20 mt-1 overflow-hidden rounded-md border border-gray-200 bg-white text-sm'
          style={{ width }}
        >
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className='flex cursor-pointer items-center gap-2 px-3 py-[10px] transition-colors hover:bg-primary-500 hover:text-white'
            >
              {dotClassName && (
                <span className={`size-2 shrink-0 rounded-full ${dotClassName(option.value)}`} />
              )}
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const toStatusOrUndefined = (value: string): ContactStatus | undefined =>
  value === '' ? undefined : (Number(value) as ContactStatus);

const CONTACT_TYPE_FILTER_OPTIONS: FilterOption[] = [
  { value: '', label: 'すべて' },
  { value: 'general', label: 'サイト全般' },
  { value: 'support_service', label: 'サービス' },
  { value: 'other', label: 'その他' },
];

const STATUS_FILTER_OPTIONS: FilterOption[] = [
  { value: '', label: 'すべて' },
  { value: '1', label: '未対応' },
  { value: '2', label: '対応中' },
  { value: '3', label: '完了' },
];

export default function ContactListForm() {
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [contactTypeFilter, setContactTypeFilter] = useState<ContactType>();
  const [statusFilter, setStatusFilter] = useState<ContactStatus>();
  const isUpdatingStatusRef = useRef(false);

  const { handleSort, handlePageChange, sort, direction, page } = useQueryParams({
    defaultSort: 'received_at',
    defaultDirection: 'desc',
  });

  const handleFilterChange =
    <T,>(setFilter: (value: T) => void) =>
    (value: T) => {
      setFilter(value);
      handlePageChange(1);
    };

  const loadContacts = async () => {
    setIsLoading(true);
    const res = await contactApiClient.index({
      sort,
      direction,
      page: page || undefined,
      contact_type: contactTypeFilter,
      status: statusFilter,
    });
    handleApiResponse(res, (data) => {
      setContacts(data.data);
      setPagination(data.pagination);
    });
    setIsLoading(false);
  };

  useEffect(() => {
    loadContacts();
  }, [sort, direction, page, contactTypeFilter, statusFilter]);

  const handleStatusChange = async (contactId: number, status: ContactStatus) => {
    if (isUpdatingStatusRef.current) {
      return;
    }

    isUpdatingStatusRef.current = true;
    setIsUpdatingStatus(true);
    try {
      const res = await contactApiClient.updateStatus(contactId, status);
      let succeeded = false;
      handleApiResponse(
        res,
        () => {
          succeeded = true;
        },
        () => {
          toast('ステータスの更新に失敗しました。');
        },
      );
      if (succeeded) {
        await loadContacts();
      }
    } finally {
      isUpdatingStatusRef.current = false;
      setIsUpdatingStatus(false);
    }
  };

  const handleCsvExport = async () => {
    setIsExporting(true);
    try {
      const params: ContactCsvExportRequest = {
        sort,
        direction,
        contact_type: contactTypeFilter,
        status: statusFilter,
      };

      const response = await contactApiClient.exportCsv(params);

      handleApiResponse<Blob, never>(
        response,
        (csvBlob) => {
          const bom = '\uFEFF';
          const blobWithBom = new Blob([bom, csvBlob], { type: 'text/csv;charset=utf-8;' });

          const blobUrl = window.URL.createObjectURL(blobWithBom);
          const anchorElement = document.createElement('a');
          anchorElement.href = blobUrl;
          const fileNamePrefix = 'お問い合わせ一覧';
          const currentDate = dayjs().format('YYYYMMDD_HHmmss');
          anchorElement.download = `${fileNamePrefix}_${currentDate}.csv`;
          document.body.appendChild(anchorElement);
          anchorElement.click();
          document.body.removeChild(anchorElement);
          window.URL.revokeObjectURL(blobUrl);
        },
        (error) => {
          logger.error('CSVのエクスポートに失敗しました。:', error);
          toast('CSVのエクスポートに失敗しました。');
        },
      );
    } catch (error) {
      logger.error('CSVのエクスポートに失敗しました。:', error);
      toast('CSVのエクスポートに失敗しました。');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <main>
      <SpinnerOverlay loading={isLoading || isUpdatingStatus} />
      <Title>お問い合わせ一覧</Title>
      <div className='mt-4 flex items-center justify-end gap-3'>
        <LabeledDropdown
          options={CONTACT_TYPE_FILTER_OPTIONS}
          value={contactTypeFilter ?? ''}
          onChange={handleFilterChange((value: string) =>
            setContactTypeFilter((value as ContactType) || undefined),
          )}
          prefix='種別：'
          width={150}
        />
        <LabeledDropdown
          options={STATUS_FILTER_OPTIONS}
          value={String(statusFilter ?? '')}
          onChange={handleFilterChange((value: string) =>
            setStatusFilter(toStatusOrUndefined(value)),
          )}
          prefix='ステータス：'
          width={190}
        />
        <Button
          size='s'
          className='!px-3 !py-2'
          onClick={handleCsvExport}
          loading={isExporting}
        >
          <SvgIconDownload color='#FFFFFF' />
        </Button>
      </div>

      <div className='mt-5 overflow-auto'>
        <table className='size-full min-w-[900px] table-fixed border-collapse border border-solid border-gray-200 text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
          <colgroup>
            <col className='w-[160px]' />
            <col className='w-[110px]' />
            <col className='w-[120px]' />
            <col />
            <col className='w-[160px]' />
            <col className='w-[120px]' />
          </colgroup>
          <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
            <tr>
              <Th
                sortable
                sortOrder={sort === 'received_at' ? (direction as 'asc' | 'desc') : null}
                onClick={() => handleSort('received_at')}
              >
                受付日時
              </Th>
              <Th
                sortable
                sortOrder={sort === 'reception_number' ? (direction as 'asc' | 'desc') : null}
                onClick={() => handleSort('reception_number')}
              >
                受付番号
              </Th>
              <Th
                sortable
                sortOrder={sort === 'contact_type' ? (direction as 'asc' | 'desc') : null}
                onClick={() => handleSort('contact_type')}
              >
                種別
              </Th>
              <Th
                sortable
                sortOrder={sort === 'company_name' ? (direction as 'asc' | 'desc') : null}
                onClick={() => handleSort('company_name')}
              >
                会社名
              </Th>
              <Th
                sortable
                sortOrder={sort === 'status' ? (direction as 'asc' | 'desc') : null}
                onClick={() => handleSort('status')}
              >
                ステータス
              </Th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact) => {
              const isDone = contact.status === CONTACT_STATUS.DONE;

              return (
                <tr
                  className={`border-b border-solid border-gray-200 ${
                    isDone ? 'bg-gray-400' : '[&:nth-child(2n)]:bg-gray-10'
                  }`}
                  key={contact.id}
                >
                  <td>
                    <div className='truncate px-3 py-4'>{contact.received_at}</div>
                  </td>
                  <td>
                    <div className='truncate px-3 py-4'>{contact.reception_number}</div>
                  </td>
                  <td>
                    <div className='px-3 py-4'>
                      <ContactTypeBadge type={contact.contact_type} />
                    </div>
                  </td>
                  <td>
                    <div className='truncate px-3 py-4'>{contact.company_name}</div>
                  </td>
                  <td>
                    <div className='px-3 py-4'>
                      <ContactStatusDropdown
                        status={contact.status}
                        disabled={isUpdatingStatus}
                        onChange={(status) => handleStatusChange(contact.id, status)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className='flex justify-center px-3'>
                      <Button
                        size='s'
                        variant='quaternary'
                        outline
                        className='whitespace-nowrap'
                        onClick={() =>
                          navigate(
                            generatePath(TENANT_URL.CONTACT_DETAIL, { id: String(contact.id) }),
                          )
                        }
                      >
                        詳細
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {contacts.length === 0 && !isLoading && (
          <p className='mt-4 text-sm'>データがありません。</p>
        )}
      </div>

      {pagination && pagination.total > 0 && (
        <>
          <p className='mt-4 text-sm text-gray-450'>
            全{pagination.total}件中 {pagination.from}-{pagination.to}件を表示
          </p>
          <div className='mt-4'>
            <PaginationComponent
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </main>
  );
}
